import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Loader2, MapPin, CalendarDays, Send, Camera, CheckCircle2, Pencil, Eye, EyeOff, MessageSquare, ClipboardEdit, Upload, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { api, fileUrl, publicUploadImage } from '../lib/api';
import { PageHeader, StatusBadge, formatDate, categoryLabel } from '../components/Shared';
import { toast } from 'sonner';

const Timeline = ({ issue }) => (
  <div className="space-y-0" data-testid="portal-timeline">
    {(issue.updates || []).slice().reverse().map((u, i, arr) => (
      <div key={u.id} className="flex gap-3">
        <div className="flex flex-col items-center">
          <span className={`h-3.5 w-3.5 rounded-full shrink-0 mt-1 ${i === 0 ? 'bg-[#C9A84C]' : u.by_resident ? 'bg-[#1a2744]' : 'bg-slate-300'}`} />
          {i < arr.length - 1 && <span className="w-0.5 flex-1 bg-slate-200" />}
        </div>
        <div className="pb-5 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={u.status} />
            <span className="text-xs text-slate-500">{formatDate(u.date)}</span>
            {u.by_resident && <span className="text-[10px] font-bold text-[#1a2744] bg-[#EAF2FF] border border-[#CFE0FF] rounded-full px-2 py-0.5">You</span>}
            {!u.public && <span className="text-[10px] font-bold text-slate-500 bg-slate-100 rounded-full px-2 py-0.5">Private</span>}
          </div>
          <p className="text-sm text-slate-700 mt-1.5 leading-relaxed break-words">{u.note}</p>
        </div>
      </div>
    ))}
  </div>
);

export default function TrackMyIssue() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [tracking, setTracking] = useState(searchParams.get('track') || '');
  const [issue, setIssue] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // action states
  const [ckl, setCkl] = useState('');
  const [note, setNote] = useState('');
  const [msg, setMsg] = useState('');
  const [notify, setNotify] = useState(true);
  const [pubTitle, setPubTitle] = useState('');
  const [pubDesc, setPubDesc] = useState('');
  const [uploading, setUploading] = useState(false);
  const [acting, setActing] = useState(false);

  const lookup = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (!email.trim() || !tracking.trim()) { setError('Enter both your email and tracking number.'); return; }
    setBusy(true);
    setError('');
    try {
      const r = await api.post('/issues/portal', { email: email.trim(), tracking_number: tracking.trim() });
      setIssue(r.data);
      setCkl(r.data.ckl_case_number || '');
      setPubTitle(r.data.public_title || '');
      setPubDesc(r.data.public_description || '');
    } catch (err) {
      setIssue(null);
      setError(err.response?.data?.detail || 'No matching report found.');
    } finally {
      setBusy(false);
    }
  }, [email, tracking]);

  const act = async (payload, successMsg) => {
    setActing(true);
    try {
      const r = await api.post('/issues/portal/action', { email: email.trim(), tracking_number: tracking.trim(), ...payload });
      setIssue(r.data);
      toast.success(successMsg);
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
      return false;
    } finally {
      setActing(false);
    }
  };

  const addPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await publicUploadImage(file);
      await act({ add_photo: res.url }, 'Photo added');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div>
      <PageHeader kicker="WARD 8 PUBLIC ISSUE TRACKER APP (PITA)" title="Track My Ward 8 Issue" subtitle="Securely view and update your own report using your email and W8 tracking number." />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <Card>
          <CardContent className="p-5 sm:p-8">
            <form onSubmit={lookup} className="space-y-4" data-testid="portal-lookup-form">
              <div>
                <label className="block font-bold text-[#1a2744] mb-2">Email used on the report</label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="h-12" data-testid="portal-email-input" />
              </div>
              <div>
                <label className="block font-bold text-[#1a2744] mb-2">W8 tracking number</label>
                <Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="W8-2026-0001" className="h-12 font-mono" data-testid="portal-tracking-input" />
              </div>
              <Button type="submit" disabled={busy} className="h-12 w-full bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="portal-lookup-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />} Find My Report
              </Button>
              {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="portal-error">{error}</p>}
            </form>
            <div className="mt-4 bg-[#EAF2FF] border border-[#CFE0FF] rounded-xl p-3 flex gap-2">
              <ShieldCheck className="h-4 w-4 text-[#1a2744] shrink-0 mt-0.5" />
              <p className="text-xs text-slate-700 leading-relaxed">You can only access a report submitted with your own email. Other residents' reports are never shown.</p>
            </div>
          </CardContent>
        </Card>

        {issue && (
          <div className="mt-6 space-y-5" data-testid="portal-issue">
            {/* Summary card */}
            <Card>
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="font-mono text-sm font-bold text-[#C9A84C]" data-testid="portal-tracking-number">{issue.tracking_number}</span>
                  <StatusBadge status={issue.status} />
                </div>
                <h2 className="font-serif font-black text-[#1a2744] text-xl mt-2 leading-snug">{issue.title}</h2>
                <p className="text-sm text-slate-700 mt-2 leading-relaxed">{issue.description}</p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs text-slate-500">
                  <span>{categoryLabel(issue.category)}</span>
                  {issue.ward && <span className="flex items-center gap-1 font-bold text-[#24406f]" data-testid="portal-ward">Ward {issue.ward}</span>}
                  {(issue.community || issue.location) && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {issue.community || issue.location}</span>}
                  <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" /> {formatDate(issue.created_at)} · {issue.days_open} day(s) open</span>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className={`text-[11px] font-bold rounded-full px-2.5 py-1 border ${issue.consent_public ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-300'}`} data-testid="portal-visibility-badge">
                    {issue.consent_public ? 'Public on board & map' : 'Private report'}
                  </span>
                  {issue.ckl_case_number && <span className="text-[11px] font-bold rounded-full px-2.5 py-1 bg-[#EAF2FF] text-[#1a2744] border border-[#CFE0FF]">CKL case #{issue.ckl_case_number}</span>}
                </div>
                {issue.cleaned_summary && (
                  <div className="mt-3 bg-[#FFFDF6] border border-[#C9A84C] rounded-lg p-3">
                    <p className="text-xs font-bold text-[#7A4E00]">Cleaned-up complaint</p>
                    <p className="text-sm text-slate-700 mt-1 leading-relaxed">{issue.cleaned_summary}</p>
                  </div>
                )}
                {issue.photos?.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    {issue.photos.map((p) => <img key={p} src={fileUrl(p)} alt="Report" className="h-24 w-full object-cover rounded-lg" />)}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Timeline */}
            <Card>
              <CardContent className="p-5 sm:p-6">
                <p className="font-serif font-bold text-[#1a2744] mb-4">Status timeline</p>
                <Timeline issue={issue} />
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardContent className="p-5 sm:p-6">
                <p className="font-serif font-bold text-[#1a2744] mb-2">Manage my report</p>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="ckl">
                    <AccordionTrigger className="font-bold text-sm text-[#1a2744]" data-testid="portal-action-ckl"><span className="flex items-center gap-2"><ClipboardEdit className="h-4 w-4 text-[#C9A84C]" /> Update CKL case number</span></AccordionTrigger>
                    <AccordionContent className="space-y-3">
                      <Input value={ckl} onChange={(e) => setCkl(e.target.value)} placeholder="Official CKL case number" className="h-11" data-testid="portal-ckl-input" />
                      <Button disabled={acting} onClick={() => act({ ckl_case_number: ckl }, 'CKL case number saved')} className="h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="portal-ckl-save">Save case number</Button>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="update">
                    <AccordionTrigger className="font-bold text-sm text-[#1a2744]" data-testid="portal-action-update"><span className="flex items-center gap-2"><Pencil className="h-4 w-4 text-[#C9A84C]" /> Add an update</span></AccordionTrigger>
                    <AccordionContent className="space-y-3">
                      <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="What's changed? e.g. The City called back, or the problem got worse…" rows={3} data-testid="portal-update-input" />
                      <Button disabled={acting || !note.trim()} onClick={async () => { if (await act({ update_note: note }, 'Update added')) setNote(''); }} className="h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="portal-update-save">Add update</Button>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="message">
                    <AccordionTrigger className="font-bold text-sm text-[#1a2744]" data-testid="portal-action-message"><span className="flex items-center gap-2"><MessageSquare className="h-4 w-4 text-[#C9A84C]" /> Send Steve a message</span></AccordionTrigger>
                    <AccordionContent className="space-y-3">
                      <Textarea value={msg} onChange={(e) => setMsg(e.target.value.slice(0, 300))} placeholder="Private message to Steve / the campaign team (max 300 characters)" rows={3} data-testid="portal-message-input" />
                      <p className="text-xs text-slate-500">{msg.length}/300 characters</p>
                      <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <Checkbox checked={notify} onCheckedChange={(v) => setNotify(!!v)} data-testid="portal-notify-checkbox" /> Notify Steve
                      </label>
                      <Button disabled={acting || !msg.trim()} onClick={async () => { if (await act({ message_to_steve: msg, notify_steve: notify }, 'Message sent to Steve')) setMsg(''); }} className="h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="portal-message-send"><Send className="h-4 w-4 mr-1.5" /> Send message</Button>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="public">
                    <AccordionTrigger className="font-bold text-sm text-[#1a2744]" data-testid="portal-action-public"><span className="flex items-center gap-2">{issue.consent_public ? <Eye className="h-4 w-4 text-[#C9A84C]" /> : <EyeOff className="h-4 w-4 text-[#C9A84C]" />} Public title, description & visibility</span></AccordionTrigger>
                    <AccordionContent className="space-y-3">
                      <div>
                        <label className="block font-bold text-sm text-[#1a2744] mb-1.5">Public title</label>
                        <Input value={pubTitle} onChange={(e) => setPubTitle(e.target.value)} className="h-11" data-testid="portal-public-title-input" />
                      </div>
                      <div>
                        <label className="block font-bold text-sm text-[#1a2744] mb-1.5">Public description</label>
                        <Textarea value={pubDesc} onChange={(e) => setPubDesc(e.target.value)} rows={3} data-testid="portal-public-desc-input" />
                      </div>
                      <Button disabled={acting} onClick={() => act({ public_title: pubTitle, public_description: pubDesc }, 'Public details saved')} className="h-11 bg-[#1a2744] text-white font-bold btn-press" data-testid="portal-public-save">Save public details</Button>
                      <div className="border-t pt-3">
                        <Button variant="outline" disabled={acting} onClick={() => act({ consent_public: !issue.consent_public }, issue.consent_public ? 'Report is now private' : 'Report is now public')} className="h-11 font-bold btn-press" data-testid="portal-visibility-toggle">
                          {issue.consent_public ? <><EyeOff className="h-4 w-4 mr-1.5" /> Make report private</> : <><Eye className="h-4 w-4 mr-1.5" /> Make report public</>}
                        </Button>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="photo">
                    <AccordionTrigger className="font-bold text-sm text-[#1a2744]" data-testid="portal-action-photo"><span className="flex items-center gap-2"><Camera className="h-4 w-4 text-[#C9A84C]" /> Upload another photo</span></AccordionTrigger>
                    <AccordionContent>
                      <label className="h-24 w-full border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-[#C9A84C] text-slate-400">
                        {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                        <span className="text-xs font-bold mt-1">Tap to add a photo</span>
                        <input type="file" accept="image/*" className="hidden" onChange={addPhoto} data-testid="portal-photo-input" />
                      </label>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                {issue.status !== 'resolved' && issue.status !== 'closed' && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" className="mt-4 h-12 w-full border-emerald-600 text-emerald-700 font-bold btn-press" data-testid="portal-resolve-button">
                        <CheckCircle2 className="h-4 w-4 mr-1.5" /> Mark resolved from my side
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="portal-resolve-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Mark this issue resolved?</AlertDialogTitle>
                        <AlertDialogDescription>This tells the campaign team the issue is fixed from your point of view. You can still add updates afterward.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => act({ mark_resolved: true }, 'Marked resolved. Thank you!')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="portal-resolve-confirm">Yes, mark resolved</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </CardContent>
            </Card>

            <p className="text-xs text-slate-500 text-center">Need a person? <Link to="/contact" className="font-bold underline decoration-[#C9A84C] decoration-2 underline-offset-4">Contact the campaign team</Link>.</p>
          </div>
        )}
      </div>
    </div>
  );
}
